import { getAuth } from 'firebase/auth';
import { Goal } from '../../types/goals';
import { goalsService } from '../goals.service';
import { updateData } from './api-wrapper.service';
import logger from '../../utils/logger';

// Local storage keys
const LAST_REMINDER_CHECK_KEY = 'MARUNE_LAST_REMINDER_CHECK';
const GOAL_REMINDERS_CACHE_KEY = 'MARUNE_GOAL_REMINDERS';
const OFFLINE_GOALS_KEY = 'MARUNE_OFFLINE_GOALS';
const NETWORK_STATUS_KEY = 'MARUNE_NETWORK_STATUS';

/**
 * Gets goals that need reminders for the current user
 */
export const getGoalsNeedingReminders = async (): Promise<Goal[]> => {
  try {
    const user = getAuth().currentUser;
    if (!user) {
      return [];
    }

    // Get current date
    const now = new Date();
    const currentTime = now.getTime();
    
    // Check if we've checked recently (in the last 5 minutes)
    const lastCheck = localStorage.getItem(LAST_REMINDER_CHECK_KEY);
    if (lastCheck) {
      const lastCheckTime = parseInt(lastCheck, 10);
      if (currentTime - lastCheckTime < 5 * 60 * 1000) { // 5 minutes
        // Return cached reminders if available
        const cachedReminders = localStorage.getItem(GOAL_REMINDERS_CACHE_KEY);
        if (cachedReminders) {
          return JSON.parse(cachedReminders);
        }
      }
    }
    
    // Update last check time
    localStorage.setItem(LAST_REMINDER_CHECK_KEY, currentTime.toString());
    
    // Check if we're online or offline
    const isOnline = navigator.onLine;
    
    let goalsNeedingReminders: Goal[] = [];
    
    if (isOnline) {
      // We're online, try to fetch from Firebase
      try {
        // Get in-progress goals with reminders enabled (RTDB).
        // RTDB goals live per-user at goals/private/{uid}; fetch that subtree
        // and filter status locally (there is no cross-user goals collection).
        // getUserGoals returns the raw RTDB records ({ id, ...data }) but is
        // typed with a leaner Goal shape; cast to the richer Goal used here.
        const allGoals = (await goalsService.getUserGoals(user.uid)) as unknown as Goal[];
        const activeGoals = allGoals.filter(
          goal => goal.status === 'not_started' || goal.status === 'in_progress'
        );

        // Filter goals that need reminders
        activeGoals.forEach((goal) => {
          // Skip goals without reminder settings
          if (!goal.reminderSettings || !goal.reminderSettings.enabled) {
            return;
          }

          // Check if it's time for a reminder
          if (isTimeForReminder(goal.reminderSettings, goal.id)) {
            goalsNeedingReminders.push(goal);
          }
        });

        // Store all active goals with reminders in localStorage for offline use
        const allGoalsWithReminders = activeGoals.filter(
          goal => goal.reminderSettings && goal.reminderSettings.enabled
        );
        localStorage.setItem(OFFLINE_GOALS_KEY, JSON.stringify(allGoalsWithReminders));

        // Update network status
        localStorage.setItem(NETWORK_STATUS_KEY, 'online');
      } catch (firebaseError) {
        logger.error('Error fetching from Firebase, falling back to offline mode:', firebaseError);
        
        // Mark as offline if Firebase request fails
        localStorage.setItem(NETWORK_STATUS_KEY, 'offline');
        
        // Fall back to offline mode
        goalsNeedingReminders = await getOfflineGoalsNeedingReminders();
      }
    } else {
      // We're offline, use cached data
      logger.info('Device is offline, using cached goals for reminders');
      localStorage.setItem(NETWORK_STATUS_KEY, 'offline');
      goalsNeedingReminders = await getOfflineGoalsNeedingReminders();
    }
    
    // Cache results
    localStorage.setItem(GOAL_REMINDERS_CACHE_KEY, JSON.stringify(goalsNeedingReminders));
    
    return goalsNeedingReminders;
  } catch (error) {
    logger.error('Error getting goals needing reminders:', error);
    // Try offline mode as last resort
    try {
      return await getOfflineGoalsNeedingReminders();
    } catch (offlineError) {
      logger.error('Offline fallback also failed:', offlineError);
      return [];
    }
  }
};

/**
 * Gets goals that need reminders from offline cache
 */
const getOfflineGoalsNeedingReminders = async (): Promise<Goal[]> => {
  try {
    const offlineGoalsStr = localStorage.getItem(OFFLINE_GOALS_KEY);
    if (!offlineGoalsStr) {
      return [];
    }
    
    const offlineGoals = JSON.parse(offlineGoalsStr) as Goal[];
    
    // Filter goals that need reminders
    return offlineGoals.filter(goal => {
      // Skip goals that are completed, failed, or abandoned
      if (goal.status === 'completed' || goal.status === 'failed' || goal.status === 'abandoned') {
        return false;
      }
      
      // Skip goals without reminder settings
      if (!goal.reminderSettings || !goal.reminderSettings.enabled) {
        return false;
      }
      
      // Check if it's time for a reminder
      return isTimeForReminder(goal.reminderSettings, goal.id);
    });
  } catch (error) {
    logger.error('Error getting offline goals needing reminders:', error);
    return [];
  }
};

/**
 * Marks a goal reminder as viewed
 * @param goalId The ID of the goal
 */
export const markGoalReminderViewed = async (goalId: string): Promise<void> => {
  try {
    const user = getAuth().currentUser;
    if (!user) {
      throw new Error('You must be logged in to mark a reminder as viewed');
    }
    
    // Set viewed timestamp in localStorage
    const reminderKey = `MARUNE_GOAL_REMINDER_${goalId}`;
    const now = Date.now();
    localStorage.setItem(reminderKey, now.toString());
    
    // Update cached reminders (whether online or offline)
    updateLocalCacheAfterViewingReminder(goalId, now);
    
    // Check if we're online
    const isOnline = navigator.onLine;
    const networkStatus = localStorage.getItem(NETWORK_STATUS_KEY);
    
    if (isOnline && networkStatus !== 'offline') {
      try {
        // We're online, update the goal reminder timestamp (RTDB).
        // updateData merges into reminderSettings, preserving sibling fields.
        await updateData(`goals/private/${user.uid}/${goalId}/reminderSettings`, {
          lastViewed: now
        });
      } catch (firebaseError) {
        logger.error('Error updating Firebase, keeping in offline queue:', firebaseError);
        addToOfflineUpdateQueue(goalId, now);
      }
    } else {
      // We're offline, queue the update for later
      logger.info('Device is offline, queueing reminder update for later');
      addToOfflineUpdateQueue(goalId, now);
    }
  } catch (error) {
    logger.error('Error marking goal reminder as viewed:', error);
    // Even if there's an error, try to update local cache
    try {
      updateLocalCacheAfterViewingReminder(goalId, Date.now());
    } catch (localError) {
      logger.error('Failed to update local cache:', localError);
    }
  }
};

/**
 * Updates all local caches after a reminder is viewed
 */
const updateLocalCacheAfterViewingReminder = (goalId: string, timestamp: number): void => {
  // Update cached reminders
  const cachedReminders = localStorage.getItem(GOAL_REMINDERS_CACHE_KEY);
  if (cachedReminders) {
    const reminders = JSON.parse(cachedReminders) as Goal[];
    const updatedReminders = reminders.filter(goal => goal.id !== goalId);
    localStorage.setItem(GOAL_REMINDERS_CACHE_KEY, JSON.stringify(updatedReminders));
  }
  
  // Also update the offline goals cache
  const offlineGoalsStr = localStorage.getItem(OFFLINE_GOALS_KEY);
  if (offlineGoalsStr) {
    const offlineGoals = JSON.parse(offlineGoalsStr) as Goal[];
    
    // Find and update the goal's lastViewed property
    const updatedGoals = offlineGoals.map(goal => {
      if (goal.id === goalId && goal.reminderSettings) {
        return {
          ...goal,
          reminderSettings: {
            ...goal.reminderSettings,
            lastViewed: timestamp
          }
        };
      }
      return goal;
    });
    
    localStorage.setItem(OFFLINE_GOALS_KEY, JSON.stringify(updatedGoals));
  }
};

/**
 * Queue for updates that need to be synced when back online
 */
const OFFLINE_UPDATE_QUEUE_KEY = 'MARUNE_OFFLINE_REMINDER_UPDATES';

/**
 * Adds a reminder update to the offline queue
 */
const addToOfflineUpdateQueue = (goalId: string, timestamp: number): void => {
  const queueStr = localStorage.getItem(OFFLINE_UPDATE_QUEUE_KEY);
  let queue: {goalId: string, timestamp: number}[] = [];
  
  if (queueStr) {
    try {
      queue = JSON.parse(queueStr);
    } catch (e) {
      logger.error('Error parsing offline update queue:', e);
    }
  }
  
  // Add this update to the queue (replace if exists)
  const existingIndex = queue.findIndex(item => item.goalId === goalId);
  if (existingIndex >= 0) {
    queue[existingIndex] = { goalId, timestamp };
  } else {
    queue.push({ goalId, timestamp });
  }
  
  localStorage.setItem(OFFLINE_UPDATE_QUEUE_KEY, JSON.stringify(queue));
};

/**
 * Syncs offline reminder updates when back online
 * This should be called when the app detects it's back online
 */
export const syncOfflineReminderUpdates = async (): Promise<void> => {
  const queueStr = localStorage.getItem(OFFLINE_UPDATE_QUEUE_KEY);
  if (!queueStr) return;
  
  try {
    const queue = JSON.parse(queueStr) as {goalId: string, timestamp: number}[];
    if (queue.length === 0) return;
    
    logger.info(`Syncing ${queue.length} offline reminder updates`);
    
    const user = getAuth().currentUser;
    if (!user) {
      logger.info('User not logged in, keeping updates in queue');
      return;
    }
    
    // Process each update one by one to handle potential errors
    const failedUpdates: typeof queue = [];

    for (const update of queue) {
      try {
        await updateData(`goals/private/${user.uid}/${update.goalId}/reminderSettings`, {
          lastViewed: update.timestamp
        });
        logger.info(`Synced reminder update for goal ${update.goalId}`);
      } catch (error) {
        logger.error(`Failed to sync reminder update for goal ${update.goalId}:`, error);
        failedUpdates.push(update);
      }
    }
    
    // Update the queue with only the failed updates
    if (failedUpdates.length > 0) {
      localStorage.setItem(OFFLINE_UPDATE_QUEUE_KEY, JSON.stringify(failedUpdates));
      logger.info(`${failedUpdates.length} updates still in queue after sync attempt`);
    } else {
      localStorage.removeItem(OFFLINE_UPDATE_QUEUE_KEY);
      logger.info('All offline reminder updates synced successfully');
    }
  } catch (error) {
    logger.error('Error syncing offline reminder updates:', error);
  }
};

/**
 * Determines if it's time to show a reminder for a goal
 * @param reminderSettings The reminder settings for the goal
 * @param goalId The ID of the goal
 * @returns True if it's time for a reminder, false otherwise
 */
function isTimeForReminder(reminderSettings: any, goalId: string): boolean {
  if (!reminderSettings || !reminderSettings.enabled) {
    return false;
  }
  
  const now = new Date();
  const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  
  // Check if we've shown a reminder recently
  const reminderKey = `MARUNE_GOAL_REMINDER_${goalId}`;
  const lastViewed = localStorage.getItem(reminderKey);
  if (lastViewed) {
    const lastViewedTime = parseInt(lastViewed, 10);
    const timeSinceLastViewed = Date.now() - lastViewedTime;
    
    // Don't show more than once per day (24 hours)
    if (timeSinceLastViewed < 24 * 60 * 60 * 1000) {
      return false;
    }
  }
  
  // Check reminder frequency
  switch (reminderSettings.frequency) {
    case 'daily':
      // Check time
      if (reminderSettings.times && reminderSettings.times.length > 0) {
        for (const time of reminderSettings.times) {
          const [hour, minute] = time.split(':').map(Number);
          
          // Allow a 5-minute window for the reminder
          if (
            (currentHour === hour && currentMinute >= minute && currentMinute < minute + 5) ||
            (currentHour === hour + 1 && minute >= 55 && currentMinute < (minute + 5) % 60)
          ) {
            return true;
          }
        }
      }
      break;
      
    case 'weekly':
      // Check day and time
      if (
        reminderSettings.days && 
        reminderSettings.days.includes(currentDay) && 
        reminderSettings.times && 
        reminderSettings.times.length > 0
      ) {
        for (const time of reminderSettings.times) {
          const [hour, minute] = time.split(':').map(Number);
          
          // Allow a 5-minute window for the reminder
          if (
            (currentHour === hour && currentMinute >= minute && currentMinute < minute + 5) ||
            (currentHour === hour + 1 && minute >= 55 && currentMinute < (minute + 5) % 60)
          ) {
            return true;
          }
        }
      }
      break;
      
    case 'custom':
      // Custom frequency allows more specific reminder patterns
      // Such as specific days of the month, interval-based reminders, etc.
      if (
        reminderSettings.customDays?.length > 0 && 
        reminderSettings.times && 
        reminderSettings.times.length > 0
      ) {
        // If customDays is provided, check if today is one of the specified days
        if (reminderSettings.customDays.includes(currentDay)) {
          for (const time of reminderSettings.times) {
            const [hour, minute] = time.split(':').map(Number);
            
            // Allow a 5-minute window for the reminder
            if (
              (currentHour === hour && currentMinute >= minute && currentMinute < minute + 5) ||
              (currentHour === hour + 1 && minute >= 55 && currentMinute < (minute + 5) % 60)
            ) {
              return true;
            }
          }
        }
      }
      
      // Check for custom interval-based reminders
      if (reminderSettings.intervalDays) {
        const lastViewed = reminderSettings.lastViewed || 0;
        const msSinceLastViewed = Date.now() - lastViewed;
        const daysSinceLastViewed = Math.floor(msSinceLastViewed / (24 * 60 * 60 * 1000));
        
        // If it's been at least intervalDays since the last reminder, 
        // and it's within the reminder time window
        if (daysSinceLastViewed >= reminderSettings.intervalDays) {
          // If no specific time is set, show reminder now
          if (!reminderSettings.times || reminderSettings.times.length === 0) {
            return true;
          }
          
          // Otherwise, check if current time matches any scheduled times
          for (const time of reminderSettings.times) {
            const [hour, minute] = time.split(':').map(Number);
            
            // Allow a 5-minute window for the reminder
            if (
              (currentHour === hour && currentMinute >= minute && currentMinute < minute + 5) ||
              (currentHour === hour + 1 && minute >= 55 && currentMinute < (minute + 5) % 60)
            ) {
              return true;
            }
          }
        }
      }
      break;
      
    default:
      break;
  }
  
  return false;
}